/**
 * Utility functions for tool result processing
 */

export interface ToolResult {
  status?: string;
  message?: string;
  error_type?: string;
  error_message?: string;
  stage?: string;
  iteration?: number;
  timestamp?: number;
  id?: string;
  [key: string]: any;
}

/**
 * Defensively checks if a tool result represents a cancellation
 * Supports multiple cancellation patterns found across different tools
 */
export function isToolCancelled(result?: ToolResult | null): boolean {
  if (!result || typeof result !== "object") {
    return false;
  }

  // Pattern 1: Direct status cancellation (unified_search, some tools)
  if (result.status === "canceled" || result.status === "cancelled") {
    return true;
  }

  // Pattern 2: User requested stop with stage (error_logging_tool, create_plan, update_plan)
  if (
    typeof result.error_message === "string" &&
    result.error_message === "User requested stop" &&
    typeof result.stage === "string" &&
    result.stage === "user_cancel"
  ) {
    return true;
  }

  // Pattern 3: Other potential cancellation messages (defensive)
  if (typeof result.error_message === "string") {
    const cancelMessages = [
      "user requested stop",
      "operation canceled",
      "operation cancelled",
      "task canceled",
      "task cancelled",
      "execution stopped",
      "stopped by user",
    ];

    const lowerMessage = result.error_message.toLowerCase();
    if (cancelMessages.some((msg) => lowerMessage.includes(msg))) {
      return true;
    }
  }

  // Pattern 4: Stage-based cancellation detection
  if (
    typeof result.stage === "string" &&
    (result.stage === "user_cancel" ||
      result.stage === "cancelled" ||
      result.stage === "canceled")
  ) {
    return true;
  }

  return false;
}

export function isNotebookBusy(result?: ToolResult | null): boolean {
  if (!result || typeof result !== "object") {
    return false;
  }

  if (
    result.error_type === "notebook_busy" ||
    result.stage === "notebook_busy"
  ) {
    return true;
  }

  const haystacks = [
    result.error_message,
    result.message,
    typeof result.execution_result?.error === "string"
      ? result.execution_result.error
      : undefined,
  ].filter((value): value is string => typeof value === "string");

  return haystacks.some((value) => value.toUpperCase().includes("NOTEBOOK_BUSY"));
}

/**
 * Gets appropriate cancellation display text based on tool type
 */
export function getCancellationText(toolName: string): {
  title: string;
  subtitle: string;
} {
  const toolDisplayNames: Record<string, { title: string; subtitle: string }> =
    {
      // Search tools
      unified_search: { title: "Search Canceled", subtitle: "Stopped by user" },

      // Plan tools
      create_plan: { title: "Blueprint Canceled", subtitle: "Stopped by user" },
      update_plan: {
        title: "Plan Update Canceled",
        subtitle: "Stopped by user",
      },
      delete_plan_step: {
        title: "Step Deletion Canceled",
        subtitle: "Stopped by user",
      },

      // Cell tools
      add_cell: {
        title: "Step Addition Canceled",
        subtitle: "Stopped by user",
      },
      replace_cell: {
        title: "Step Update Canceled",
        subtitle: "Stopped by user",
      },
      edit_cell: {
        title: "Analysis Refinement Canceled",
        subtitle: "Stopped by user",
      },
      delete_cells: {
        title: "Step Deletion Canceled",
        subtitle: "Stopped by user",
      },
      restart_notebook: {
        title: "Notebook Restart Canceled",
        subtitle: "Stopped by user",
      },

      // Documentation tools
      get_documentation: {
        title: "Documentation Canceled",
        subtitle: "Stopped by user",
      },
      get_visual_templates: {
        title: "Template Loading Canceled",
        subtitle: "Stopped by user",
      },
      get_cell_details: {
        title: "Analysis Canceled",
        subtitle: "Stopped by user",
      },

      // Chart tools
      add_drawings: {
        title: "Drawing Addition Canceled",
        subtitle: "Stopped by user",
      },
      delete_drawings: {
        title: "Drawing Deletion Canceled",
        subtitle: "Stopped by user",
      },
      set_viewport: {
        title: "Viewport Change Canceled",
        subtitle: "Stopped by user",
      },

      consult_planning_agent: {
        title: "Planning Canceled",
        subtitle: "Stopped by user",
      },

      // Agent tools
      error_logging_tool: {
        title: "Agent Paused",
        subtitle: "Do you wish to continue?",
      },
      fast_forward: {
        title: "Fast Forward Canceled",
        subtitle: "Stopped by user",
      },
      await_user_execution: {
        title: "User Action Canceled",
        subtitle: "Stopped by user",
      },
      generate_summary: {
        title: "Summary Generation Canceled",
        subtitle: "Stopped by user",
      },
    };

  return (
    toolDisplayNames[toolName] || {
      title: "Operation Canceled",
      subtitle: "Stopped by user",
    }
  );
}

/**
 * Determines if a tool result represents a max iterations case (special handling)
 */
export function isMaxIterations(result?: ToolResult | null): boolean {
  if (!result || typeof result !== "object") {
    return false;
  }

  return (
    typeof result.error_message === "string" &&
    result.error_message === "Max agent turns reached" &&
    typeof result.stage === "string" &&
    result.stage === "max_iterations_guard"
  );
}
